import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { usePutOperationMutation } from "redux/api/operationApi";
import { setCurrentOperation } from "redux/features/operationSlice";

const PublicOfficeDropdown: React.FC<{ nextStep: () => void, previousStep: () => void }> = ({ nextStep, previousStep }) => {
  const dispatch = useDispatch();
    const { formatMessage } = useIntl();
  
  const [
    updateOperation,
    {
      data: postedOperationData,
      isSuccess: isPostedOperationSuccess,
      error: apiError,
    },
  ] = usePutOperationMutation();

  const [hasPublicOffice, setHasPublicOffice] = useState("");
  const [publicOffice, setPublicOffice] = useState("");
  const [error, setError] = useState(false);
  const [initialState, setInitialState] = useState<{ hasPublicOffice: string, publicOffice: string } | null>(null);
  
  const operation = useSelector((state: any) => {
    return state.operationState?.currentOperation?.data || state.operationState?.currentOperation;
  });

  useEffect(() => {
    if (operation) {
      const normalizedPublicOffice = operation.publicOffice?.trim() || "";
  
      const initialHasPublicOffice =
        normalizedPublicOffice === "no" ? "no" : 
        normalizedPublicOffice ? "yes" : "";
  
      const initialPublicOffice =
        normalizedPublicOffice === "no" ? "" : normalizedPublicOffice;
  
      setHasPublicOffice(initialHasPublicOffice);
      setPublicOffice(initialPublicOffice);
  
      setInitialState({
        hasPublicOffice: initialHasPublicOffice,
        publicOffice: initialPublicOffice,
      });
    }
  }, [operation]);
  
  
  

  if (!operation) {
    return <p>{formatMessage({ id: "public.office.error" })}</p>;
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    event.preventDefault();
    setHasPublicOffice(event.target.value as string);
  };
  const handleSubmit = () => {
    if (!hasPublicOffice || (hasPublicOffice === "yes" && !publicOffice.trim())) {
      setError(true);
      return;
    }
  
    if (initialState && hasPublicOffice === initialState.hasPublicOffice && publicOffice === initialState.publicOffice) {
      nextStep();
      return;
    }
  
    const updatedOperation = {
      ...operation,
      publicOffice: hasPublicOffice === "no" ? "no" : publicOffice,
    };
  
    const formData = new FormData();
    formData.append("data", JSON.stringify(updatedOperation));
  
    updateOperation({ id: operation.id, operationToUpdate: formData });
  };
  
  if(isPostedOperationSuccess && postedOperationData) {
    dispatch(setCurrentOperation(postedOperationData));
    nextStep();
  }
  return (
    <Card sx={{ p: 2 }}>
      <CardHeader
        title={
          <Typography variant="h1" align="center">
            {formatMessage({ id: "public.office.title" })}
          </Typography>
        }
      />
        <CardContent>
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "start",
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              <FormControl
                error={error === true}
                sx={{
                  minWidth: 300,

                  borderRadius: "2px",
                  padding: "5px",
                  gap: "20px",
                }}
              >
                <Typography
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {formatMessage({ id: "public.office.question" })}
                </Typography>
                <Select
                  id="public-office-select"
                  value={hasPublicOffice}
                  onChange={handleChange}
                  displayEmpty
                  sx={{
                    border: "1.5px solid black",
                    width: "100%",
                    backgroundColor: "white", // Establecer fondo blanco
                    "& .MuiSelect-root": {
                      backgroundColor: "white", // Fondo para la raíz del select
                    },
                    "&:hover": {
                      backgroundColor: "white", // Fondo al pasar el mouse
                    },
                    "&.Mui-focused": {
                      border: "1.5px solid black", // Elimina el borde rojo en el estado focused
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none", // Si usas `OutlinedInput`, asegúrate de eliminar el borde extra
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        border: "1px solid black", // Borde del menú desplegable
                        borderRadius: "5px",
                        marginTop: "4px",
                        boxShadow: "0px 4px 6px rgba(0,0,0,0.1)", // Sombra para un diseño más limpio
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    <Typography sx={{ color: "#B3B3B3" }}>{formatMessage({ id: "public.office.title" })}</Typography>
                  </MenuItem>
                  <MenuItem value={"yes"}>{formatMessage({ id: "public.office.yes" })}</MenuItem>
                  <MenuItem value={"no"}>{formatMessage({ id: "public.office.no" })}</MenuItem>
                </Select>
                <TextField
                  placeholder={formatMessage({ id: "public.office.placeholder" })}
                  variant="standard"
                  InputProps={{
                    sx: {
                      "&::placeholder": {
                        opacity: 1,
                      },
                    },
                  }}
                  value={publicOffice}
                  disabled={hasPublicOffice !== "yes"}
                  onChange={(e) => setPublicOffice(e.target.value)}
                />
                {/*Mostrar mensaje de error si no se ha seleccionado nada*/}
                {error && (
                  <FormHelperText sx={{ color: "red" }}>
                    {formatMessage({ id: "public.office.error.uncomplete" })}
                  </FormHelperText>
                )}
              </FormControl>
            </Box><Grid container spacing={2} justifyContent="space-between">
              <Grid item xs={12} lg={6}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={previousStep}
                  sx={{
                    mt: 2,
                    backgroundColor: "#FFF",
                    display: {
                      xs: "none", // Ocultar en pantallas pequeñas (móviles)
                      sm: "none", // Ocultar en tablets
                      md: "block", // Mostrar en pantallas medianas y grandes
                    },
                  }}
                >
                  {formatMessage({ id: "buttons.back" })}
                </Button>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    backgroundColor: "black",
                    color: "white",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                    alignSelf: "center",
                  }}
                  onClick={handleSubmit}
                >
                  {formatMessage({ id: "buttons.continue" })}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
    </Card>
  );
};
export default PublicOfficeDropdown;
